import React from 'react';
import { cn } from '@/utils/cn';
import type { MetaSpecialAdCategory } from '@/services/api/metaAds';

interface MetaSpecialAdCategoriesStepProps {
  categories: MetaSpecialAdCategory[];
  onChange: (categories: MetaSpecialAdCategory[]) => void;
  errors?: {
    categories?: string;
  };
}

const REGULATED: Array<{ value: Exclude<MetaSpecialAdCategory, 'NONE'>; label: string; description: string }> = [
  {
    value: 'HOUSING',
    label: 'Housing',
    description: 'Ads about housing opportunities (rentals, sales, listings, brokerage services, mortgages).',
  },
  {
    value: 'EMPLOYMENT',
    label: 'Employment',
    description: 'Ads about jobs, job training, internships, or other employment opportunities.',
  },
  {
    value: 'CREDIT',
    label: 'Credit',
    description: 'Ads about credit card offers, auto loans, personal or business loan services, mortgages.',
  },
  {
    value: 'ISSUES_ELECTIONS_POLITICS',
    label: 'Social issues, elections, or politics',
    description: 'Ads about social issues, electoral or political content (varies by country).',
  },
];

/**
 * Step 5 — Meta Special Ad Categories (policy declaration).
 *
 * Meta auto-disapproves a campaign that should have declared HOUSING /
 * EMPLOYMENT / CREDIT / ISSUES_ELECTIONS_POLITICS but didn't (and restricts
 * available targeting on declared campaigns — e.g. no age/gender targeting
 * on housing). Defaulting silently is a policy footgun, so this step
 * REQUIRES an explicit selection — even if that selection is 'NONE'.
 *
 * 'NONE' is exclusive: combining NONE with HOUSING is meaningless and the
 * backend model_validator rejects it. The UI enforces the same at the
 * checkbox handler.
 */
export const MetaSpecialAdCategoriesStep: React.FC<MetaSpecialAdCategoriesStepProps> = ({
  categories,
  onChange,
  errors,
}) => {
  const isNone = categories.length === 1 && categories[0] === 'NONE';

  const toggleNone = () => {
    onChange(isNone ? [] : ['NONE']);
  };

  const toggleRegulated = (value: Exclude<MetaSpecialAdCategory, 'NONE'>) => {
    // Toggling any regulated category clears NONE — they're mutually exclusive.
    const next = categories.filter((c) => c !== 'NONE');
    if (next.includes(value)) {
      onChange(next.filter((c) => c !== value));
    } else {
      onChange([...next, value]);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Special Ad Categories
        </h3>
        <p className="text-sm text-gray-600">
          Meta requires explicit declaration when ads promote housing, employment, credit, or political content.
          Misdeclared ads are auto-disapproved by Meta.
        </p>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <p>
          You must select one option below. <strong>None of the above</strong> is the explicit
          "not a regulated category" answer — choose it deliberately if your campaign doesn't
          fall under any category. Selecting a regulated category will restrict available
          targeting options on the ad set level.
        </p>
      </div>

      <button
        type="button"
        onClick={toggleNone}
        aria-pressed={isNone}
        className={cn(
          'w-full text-left p-4 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
          isNone
            ? 'border-blue-600 bg-blue-50'
            : 'border-gray-200 hover:bg-gray-50 bg-white',
        )}
      >
        <div className="text-sm font-medium text-gray-900">None of the above</div>
        <div className="text-xs text-gray-500 mt-1">
          Campaign isn't about housing, employment, credit, or political content.
        </div>
      </button>

      <div className="space-y-2">
        {REGULATED.map((cat) => {
          const checked = categories.includes(cat.value);
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => toggleRegulated(cat.value)}
              aria-pressed={checked}
              className={cn(
                'w-full text-left p-4 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
                checked
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50 bg-white',
              )}
            >
              <div className="text-sm font-medium text-gray-900">{cat.label}</div>
              <div className="text-xs text-gray-500 mt-1">{cat.description}</div>
            </button>
          );
        })}
      </div>

      {errors?.categories && (
        <p role="alert" className="text-sm text-red-600">
          {errors.categories}
        </p>
      )}
    </div>
  );
};
